import {computed, makeObservable, observable} from "mobx";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {dateToEsri, esriToDate} from "../utils/esri-utils";
import {AbstractModel} from "./abstract-model";

export interface ProcessLogProps {
    objectId?: number;
    guid?: string;
    projectId?: number;
    field?: string;
    timeStamp?: Date;
    statusFrom?: number;
    statusTo?: number;
    message: string;
    type: number;
    cycle: string;
    creator?: string;
    createdAt?: Date;
    editor?: string;
    updatedAt?: Date;
}

/**
 * Prozess für Projekte
 *
 * Serializer siehe serializers/project-process-json.ts
 */
@JsonObject()
export class ProcessLog extends AbstractModel {

    static readonly comparableAttributes: string[] = [
        "GUID", "PROJ_ID", "FIELD", "STATUS_FROM", "STATUS_TO", "MESSAGE", "TYPE", "CYCLE"
    ];
    // workaround, because feature template doesn't seem to work for table features
    static readonly prototypeAttributes: string[] = [
        "OBJECTID", "GUID", "PROJ_ID", "FIELD", "TIME_STAMP", "STATUS_FROM", "STATUS_TO", "MESSAGE", "TYPE", "CYCLE",
        "CREATED_USER", "CREATED_AT", "UPDATED_USER", "UPDATED_AT"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "OBJECTID"],
        ["guid", "GUID"],
        ["projectId", "PROJ_ID"],
        ["field", "FIELD"],
        ["timeStamp", "TIME_STAMP"],
        ["statusFrom", "STATUS_FROM"],
        ["statusTo", "STATUS_TO"],
        ["message", "MESSAGE"],
        ["type", "TYPE"],
        ["cycle", "CYCLE"],
        ["creator", "CREATED_USER"],
        ["createdAt", "CREATED_AT"],
        ["editor", "UPDATED_USER"],
        ["updatedAt", "UPDATED_AT"]
    ]);

    class = "ProjectProcess";
    typeLabel = "Prozesslog";

    static sortByDate(a: ProcessLog, b: ProcessLog) {
        if (a?.timeStamp && b?.timeStamp && a?.timeStamp > b?.timeStamp) {
            return -1;
        }
        if (a?.timeStamp && b?.timeStamp && a?.timeStamp < b?.timeStamp) {
            return 1;
        }

        // dates must be equal
        return 0;
    }

    /**
     * GUID
     */
    @JsonProperty({name: "GUID"})
        guid: string;

    /**
     * Projektschlüssel
     */
    @JsonProperty({name: "PROJ_ID"})
        projectId: number;

    /**
     * Feldname
     */
    @JsonProperty({name: "FIELD"})
        field: string;

    /**
     * Zeitpunkt
     */
    @JsonProperty({
        name: "TIME_STAMP",
        beforeDeserialize: esriToDate,
        afterSerialize: dateToEsri
    })
        timeStamp: Date;

    /**
     * Quellstatus
     *
     */
    @JsonProperty({name: "STATUS_FROM"})
        statusFrom: number;

    /**
     * Zielstatus
     *
     */
    @JsonProperty({name: "STATUS_TO"})
        statusTo: number;

    /**
     * Meldung
     */
    @JsonProperty({name: "MESSAGE"})
        message: string;

    /**
     * Meldungstyp
     */
    @JsonProperty({name: "TYPE"})
        type: number;

    /**
     * Prozesszyklus
     */
    @JsonProperty({name: "CYCLE"})
        cycle: string;


    constructor(props?: ProcessLogProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        // make observable after setting properties to avoid 'strict' messages
        makeObservable(this, {
            objectId: observable, // do observe to reload when object is saved
            guid: observable,
            projectId: observable,
            field: observable,
            timeStamp: observable,
            statusFrom: observable,
            statusTo: observable,
            message: observable,
            type: observable,
            cycle: observable,

            isAlterable: computed,

            creator: observable,
            createdAt: observable,
            editor: observable,
            updatedAt: observable,
            isNewRecord: computed
        });
    }

    get comparableAttributes(): string[] {
        return ProcessLog.comparableAttributes;
    }

    get isAlterable(): boolean {
        return true;
    }

}

